// Core
import React, { useState } from 'react';

// Libraries
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useNavigate, NavigateFunction } from 'react-router-dom';

// Plugins
import {
  requestVantageApi,
  RequestMethod,
  isHTTPResponseError,
} from '../../plugins/request';
import { isValidEmail } from '../../plugins/general';

// Hooks
import { useBreakpoint } from '../../hooks/useBreakpoint';

// Layout
import { Alert, AlertType } from '../../layout/elements/Alert';
import {
  Button,
  ButtonSize,
  ButtonColor,
  ButtonType,
} from '../../layout/elements/Button';
import { Input } from '../../layout/elements/Input';
import { LeftPanelImageRight } from '../../layout/LeftPanelImageRight';
import { Paragraph, ParagraphAlign } from '../../layout/elements/Paragraph';
import { showToast, ToastType } from '../../layout/elements/Toast';
import {
  Title,
  TitleAlign,
  TitleSize,
  TitleWeight,
} from '../../layout/elements/Title';
import { AlertLine, ArrowLeft } from '../../layout/icons';

// Style
import {
  AlertContainer,
  BoldText,
  Description,
  FormContentContainer,
  InlineButton,
  InputEmailContainer,
  PasswordRecoveryContentContainer,
  TitleContainer,
} from './PasswordRecovery.style';

// Types
import { Theme } from '../../types/theme';

function PasswordRecovery(): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const theme: Theme = useTheme();
  const { isXs, isSm, isMd } = useBreakpoint();

  /* ***********************************************************************************************
  ***************************************** LOCAL STATES *******************************************
  *********************************************************************************************** */

  interface PasswordRecoveryFormState {
    email: string | null;
    error: string | null;
    success: boolean;
    isLoading: number | null | true;
  }

  const initialPasswordRecoveryFormState: PasswordRecoveryFormState = {
    email: null,
    error: null,
    success: false,
    isLoading: null,
  };

  const [
    passwordRecoveryFormState,
    setPasswordRecoveryFormState,
  ] = useState<PasswordRecoveryFormState>(
    initialPasswordRecoveryFormState,
  );

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  // Function to handle errors
  const handlePasswordRecoveryErrors = (error: unknown): void => {
    let errorMessage: string = t('common.defaultError');

    // Check if the error is an HTTP response error
    if (isHTTPResponseError(error)) {
      if (error.message.includes('Network Error')) {
        errorMessage = t('common.networkError');
      } else if (error.response.status === 422) {
        errorMessage = t('view.passwordRecovery.invalidEmail');
      }
    }

    setPasswordRecoveryFormState((prevState: PasswordRecoveryFormState) => ({
      ...prevState,
      error: errorMessage,
      isLoading: null,
    }));
  };

  /**
   * Submits the password recovery form to send recovery email.
   *
   * @returns {Promise<void>} A promise that resolves when the recovery process is complete.
   */
  const submitPasswordRecoveryForm = async (): Promise<void> => {
    if (!passwordRecoveryFormState.email) {
      return;
    }

    setPasswordRecoveryFormState((prevState: PasswordRecoveryFormState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));

    try {
      await requestVantageApi.users({
        method: RequestMethod.POST,
        path: '/auth/recovery-password',
        body: {
          email: passwordRecoveryFormState.email,
        },
        handleErrors: false,
      });

      // Show success message
      showToast({
        text: t('view.passwordRecovery.emailSentSuccess'),
        type: ToastType.success,
      });

      setPasswordRecoveryFormState((prevState: PasswordRecoveryFormState) => ({
        ...prevState,
        success: true,
        isLoading: null,
      }));
    } catch (error: unknown) {
      handlePasswordRecoveryErrors(error);
    }
  };

  /**
   * Updates the email field in the password recovery form state.
   *
   * @param {string} field - The name of the field to update.
   * @param {string | null | number} [value] - The new value for the field.
   * @returns {void}
   */
  const updatePasswordRecoveryField = (
    field: keyof PasswordRecoveryFormState,
    value?: string | null | number,
  ): void => {
    setPasswordRecoveryFormState((prevState: PasswordRecoveryFormState) => ({
      ...prevState,
      [field]: value as string | null,
      error: null,
    }));
  };

  /**
   * Navigates back to the login page.
   */
  const navigateBackToLogin = (): void => {
    navigate('/');
  };

  /**
   * Determines if the password recovery form submit button should be disabled.
   * The button is disabled if the email field is null, undefined, empty, or invalid format.
   * @type {boolean}
   */
  const isDisabledToSubmitPasswordRecoveryForm: boolean = !isValidEmail(
    passwordRecoveryFormState.email,
  );

  return (
    <LeftPanelImageRight>
      <PasswordRecoveryContentContainer>
        <FormContentContainer>
          <TitleContainer>
            <Title
              align={TitleAlign.center}
              size={(isXs || isSm || isMd) ? TitleSize.xxs : TitleSize.xs}
              weight={TitleWeight.bold}
            >
              {t('view.passwordRecovery.title')}
            </Title>
          </TitleContainer>

          {passwordRecoveryFormState.success ? (
            <>
              <Description>
                <Paragraph
                  align={ParagraphAlign.center}
                  color={theme.color.passwordRecovery.descriptionTextColor}
                >
                  <Trans
                    i18nKey="view.passwordRecovery.emailSentConfirmation"
                    values={{ email: passwordRecoveryFormState.email }}
                    components={{ b: <BoldText /> }}
                  />
                </Paragraph>
              </Description>

              <Description>
                <Paragraph
                  align={ParagraphAlign.center}
                  color={theme.color.passwordRecovery.descriptionTextColor}
                >
                  <Trans
                    i18nKey="view.passwordRecovery.resendInstructions"
                    components={{
                      action: (
                        <InlineButton
                          color={ButtonColor.primary}
                          onClick={submitPasswordRecoveryForm}
                          size={ButtonSize.sm}
                          type={ButtonType.link}
                        >
                          {t('common.clickHere')}
                        </InlineButton>
                      ),
                    }}
                  />
                </Paragraph>
              </Description>
            </>
          ) : (
            <>
              <Description>
                <Paragraph
                  align={ParagraphAlign.center}
                  color={theme.color.passwordRecovery.descriptionTextColor}
                >
                  {t('view.passwordRecovery.instructionsParagraph')}
                </Paragraph>
              </Description>

              {passwordRecoveryFormState.error && (
                <AlertContainer>
                  <Alert
                    closable={false}
                    description={passwordRecoveryFormState.error}
                    icon={<AlertLine />}
                    testId="errorAlert"
                    type={AlertType.error}
                  />
                </AlertContainer>
              )}

              <InputEmailContainer>
                <Input
                  error={passwordRecoveryFormState.error !== null}
                  label={`${t('common.email')}`}
                  onChange={(value?: string | number | null) => updatePasswordRecoveryField('email', value)}
                  placeholder={`${t('common.email')}`}
                  testId="input-email"
                  value={passwordRecoveryFormState.email}
                  onPressEnter={
                    !isDisabledToSubmitPasswordRecoveryForm
                      ? submitPasswordRecoveryForm
                      : undefined
                    }
                />
                <Button
                  size={ButtonSize.lg}
                  loading={passwordRecoveryFormState.isLoading}
                  disabled={isDisabledToSubmitPasswordRecoveryForm}
                  loadingText={`${t('view.passwordRecovery.sendingLink')}`}
                  onClick={submitPasswordRecoveryForm}
                  testId="send-link-button"
                >
                  {t('view.passwordRecovery.sendLink')}
                </Button>
              </InputEmailContainer>
            </>
          )}

          <Button
            color={ButtonColor.neutral}
            onClick={navigateBackToLogin}
            size={ButtonSize.lg}
            type={ButtonType.link}
            testId="back-to-login-button"
          >
            <ArrowLeft />
            {t('view.passwordRecovery.backToLogin')}
          </Button>
        </FormContentContainer>
      </PasswordRecoveryContentContainer>
    </LeftPanelImageRight>
  );
}

export { PasswordRecovery };
